import "./PageHeader.css";

import { useCallback, useRef, useState } from "react";

import { DateTimeHelper } from "../../../../lib/utils/DateTimeHelper";
import { LockBadge } from "../../../locks/components/LockBadge";
import { LockMark } from "../../../locks/models/LockDTO";
import { PageMetadataTag } from "../../models/PageMetadata";
import { TagSVG } from "../../../../lib/assets/icons";
import { useOutsideComponentClickTrigger } from "../../../../lib/hooks/useOutsideComponentClickTrigger";

interface IPageHeaderProps {
    title: string;
    author: string;
    date: string;
    /** Effective expedition of the page (metadata cascade), when it has one. */
    expedition?: string;
    /** Effective tags of the page (own, inherited or inferred). */
    tags?: PageMetadataTag[];
    /** The page's lock badge (own or inherited), when it is locked. */
    lock?: LockMark;
    children: React.ReactNode;
}

function tagTitle(tag: PageMetadataTag): string {
    if (tag.source === "own") return tag.name;
    const confidence = tag.confidence !== undefined ? ` ${Math.round(tag.confidence * 100)}%` : "";
    return `${tag.name} (${tag.source}${confidence})`;
}

export function PageHeader(props: IPageHeaderProps) {

    const [isTagsOpen, setIsTagsOpen] = useState(false);
    const tagsRef = useRef<HTMLDivElement>(null);
    const tags = props.tags ?? [];
    const hasTags = tags.length > 0;

    const closeTags = useCallback(() => setIsTagsOpen(false), []);
    useOutsideComponentClickTrigger(tagsRef, closeTags);

    return (
        <div className="page-header">
            <div className="page-header-details">
                <div className="page-header-details-title">
                    <h1>{props.title}<LockBadge mark={props.lock} /></h1>
                </div>
                <div className="page-header-details-meta">
                    <div className="page-header-details-subinfo">
                        <span>{props.author}</span>
                        <span className="page-header-details-dot" />
                        <span>{DateTimeHelper.formatDateTimeAsTimeAgo(props.date)}</span>
                    </div>
                </div>
                {(hasTags || props.expedition) && (
                    <div className="page-header-details-expedition-row">
                        {props.expedition && (
                            <div className="page-header-details-expedition">
                                <span>Expedition</span>
                                <strong title={props.expedition}>{props.expedition}</strong>
                            </div>
                        )}
                        {hasTags && (
                            <div className="page-header-tags-wrapper" ref={tagsRef}>
                                <button
                                    className="page-header-tags-button"
                                    type="button"
                                    title="Show tags"
                                    aria-label={`Show tags (${tags.length})`}
                                    aria-expanded={isTagsOpen}
                                    onClick={() => setIsTagsOpen(!isTagsOpen)}
                                >
                                    <TagSVG />
                                    <span>{tags.length}</span>
                                </button>
                                {isTagsOpen && (
                                    <div className="page-header-tags-popover">
                                        {tags.map((tag) => (
                                            <span key={tag.name} className="page-header-details-tag" title={tagTitle(tag)}>
                                                {tag.name}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
            <div className="page-header-actions">
                {props.children}
            </div>
        </div>
    )
}
